import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { LocationPros } from "../../../../types/baseTypes";
import { RootState } from "../../../../redux/store";
import {
  setRoomRentalLocation,
  setSelectedLocation,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { cityOf, districtOf } from "../../../../util/locationAddress";

const triggerClassName =
  "w-full h-[62px] text-base border rounded-[12px] bg-white text-slate-900 border-primary hover:border-primary";

/**
 * City + district selection of the room-rental AND venue search masks.
 *
 * ONE component for both flows — the two previous per-flow copies were
 * byte-identical apart from comments and had already started to drift that
 * way (innodom review, session to-do 7).
 *
 * Single-location tenants (one city, one district — innodom, Neuland) see
 * both fields pre-filled and disabled: the user reads where they are booking
 * without being offered a choice that does not exist. With several cities or
 * districts the fields stay interactive exactly as before.
 */
export default function CityAndDistrictSelection({
  locations,
}: Readonly<{
  locations: LocationPros[];
}>) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const roomRentalLocation = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation
  );

  const cities = Array.from(
    new Set(locations.map((loc) => cityOf(loc)).filter(Boolean)),
  );

  // Auto-select city if there's only one
  React.useEffect(() => {
    if (cities.length === 1 && !roomRentalLocation.city) {
      dispatch(setRoomRentalLocation({ city: cities[0], district: "all" }));
    }
  }, [cities, dispatch, roomRentalLocation.city]);

  const districts = roomRentalLocation.city
    ? Array.from(
        new Set(
          locations
            .filter((loc) => cityOf(loc) === roomRentalLocation.city)
            .map((loc) => districtOf(loc))
            .filter(Boolean) as string[]
        )
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    : [];

  // A lone district is fixed like a lone city — but DISPLAY-ONLY: the stored
  // value stays "all". `district === 'all'` switches the fetch from
  // by-city to by-city-and-district downstream (fetchVenues/ApartmentsFlow),
  // and a location with no maintained district would silently drop out of a
  // by-district query. Showing the lone name while querying by city keeps
  // the result set exactly as before.
  const onlyDistrict = districts.length === 1 ? districts[0] : null;

  const handleCityChange = (value: string) => {
    dispatch(setRoomRentalLocation({ city: value, district: "all" }));
    dispatch(setSelectedLocation(null));
  };

  const handleDistrictChange = (value: string) => {
    dispatch(
      setRoomRentalLocation({
        city: roomRentalLocation.city,
        district: value,
      })
    );
  };

  // Prefer the auto-selected values synchronously so the fields don't briefly
  // render as disabled-empty before the useEffect dispatches land.
  const cityValue =
    roomRentalLocation.city ?? (cities.length === 1 ? cities[0] : "");
  const cityDisabled = cities.length === 1;
  const districtValue =
    onlyDistrict ??
    (roomRentalLocation.district === "all"
      ? "all"
      : roomRentalLocation.district || undefined);
  const districtDisabled = roomRentalLocation.city === null || onlyDistrict !== null;
  // The lone district must be a rendered option for the Select to display it
  // even while the stored value is "all"; with several districts the list is
  // unchanged.

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-slate-900">
          {t("book_location.location_city")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <Select
          value={cityValue || undefined}
          onValueChange={handleCityChange}
          disabled={cityDisabled}
        >
          <SelectTrigger
            className={triggerClassName}
            aria-label={t("book_location.location_city")}
          >
            <div className="flex items-center gap-2 w-full">
              <MapPin className="h-5 w-5 text-slate-600 shrink-0" />
              <SelectValue placeholder={t("book_location.location_city")} />
            </div>
          </SelectTrigger>
          <SelectContent>
            {cities.map((city) => (
              <SelectItem key={city} value={city}>
                {city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-slate-900">
          {t("book_location.district")}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <Select
          value={districtValue}
          onValueChange={handleDistrictChange}
          disabled={districtDisabled}
        >
          <SelectTrigger
            className={triggerClassName}
            aria-label={t("book_location.district")}
          >
            <SelectValue placeholder={t("book_location.district")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("book_location.all_districts")}
            </SelectItem>
            {districts.map((district) => (
              <SelectItem key={district} value={district}>
                {district}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
